import Compressor from 'compressorjs'
import { Toast } from 'vant'
import { upload } from '@/api/http.js'

/**
 * 压缩图片
 * @param {File} file 原始文件
 * @returns {Promise<File>} 压缩后的文件
 */
export const compressImage = (file) => {
  return new Promise((resolve, reject) => {
    new Compressor(file, {
      quality: 0.4, // 压缩质量
      checkOrientation: false, // 图片翻转，默认为false
      success(result) {
        resolve(new File([result], result.name, { type: result.type }))
      },
      error(e) {
        console.log('compress error', e)
        reject(e)
      }
    })
  })
}

/**
 * 压缩并上传文件，返回原始响应（业务码由调用方处理）
 * @param {string} url 上传接口地址
 * @param {File} rawFile 待上传文件
 * @param {object} options 可选配置：compress 是否压缩（默认 true）、message 加载提示、extra 额外表单字段、
 *   maxSize 压缩后允许的最大字节数（超出按业务码 04 返回，不发请求）
 * @returns {Promise<object>} 接口原始响应数据
 */
export const uploadFile = async (url, rawFile, options = {}) => {
  const { compress = true, message = '图片上传中，\n请耐心等待…', extra = {}, maxSize = 0 } = options
  Toast.loading({
    duration: 0,
    forbidClick: true,
    message,
  })
  try {
    const file = compress ? await compressImage(rawFile) : rawFile
    if (maxSize && file.size > maxSize) {
      return { resultCode: '04', resultDesc: '图片大小不能超过 5MB，请更换后重试' }
    }
    const formData = new FormData()
    formData.append('file', file)
    Object.keys(extra).forEach(key => formData.append(key, extra[key]))
    return await upload(url, formData, { raw: true, silent: true })
  } finally {
    Toast.clear()
  }
}

// OCR 图片类型前置检查（接口文档：身份证/银行卡 OCR 仅支持 JPG/JPEG/PNG）
// 返回空串表示通过，否则为提示文案
export const checkImageType = (rawFile) => {
  const ok = /^image\/(jpeg|png)$/i.test(rawFile.type || '') || /\.(jpe?g|png)$/i.test(rawFile.name || '')
  return ok ? '' : '仅支持 JPG/PNG 格式图片'
}

const OCR_MAX_SIZE = 5 * 1024 * 1024

// OCR 包装：类型不符直接按 04 返回，不发请求；大小在压缩后校验
const ocrUpload = (url, rawFile) => {
  const typeErr = checkImageType(rawFile)
  if (typeErr) {
    return Promise.resolve({ resultCode: '04', resultDesc: typeErr })
  }
  return uploadFile(url, rawFile, { maxSize: OCR_MAX_SIZE })
}

// 普通文件上传，返回 { url }
export const uploadSingleFile = (rawFile) =>
  uploadFile('/unicom/cluster/file/uploadSingleFile', rawFile)

// 身份证 OCR 识别
export const uploadIDOCR = (rawFile) =>
  ocrUpload('/unicom/cluster/file/uploadSingleFileAndIDOCR', rawFile)

// 银行卡 OCR 识别
export const uploadBankOCR = (rawFile) =>
  ocrUpload('/unicom/cluster/file/uploadSingleFileAndBankOCR', rawFile)

// 场景照片上传（该接口要求 uid/token 放在表单内鉴权，FormData 不走 token header 逻辑）
export const uploadScenePhoto = (rawFile) => {
  let extra = {}
  try {
    const userData = JSON.parse(localStorage.getItem('userData')) || {}
    extra = { uid: userData.uid || '', token: userData.token || '' }
  } catch (e) { /* localStorage 脏数据时按未登录处理，由后端返回登录失败 */ }
  return uploadFile('/unicom/cluster/file/uploadScenePhoto', rawFile, { extra })
}
